import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, X, Brain, Mic, MicOff } from 'lucide-react';
import toast from 'react-hot-toast';
import {
    getAreas,
    getUsers,
    createObservation,
    // getImageDescriptionFromUrl
} from '../../services/observationAPI';
import { uploadEvidenceToServer } from '../../services/operations/profileAPI';
import { useSelector } from 'react-redux';

const SubmitObservation = () => {
    const navigate = useNavigate();
    const [areas, setAreas] = useState([]);
    const [users, setUsers] = useState([]); // Keep fetching users in case they are needed elsewhere or in future updates
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // State for photo handling
    const [photo, setPhoto] = useState(null); // The actual File object
    const [uploadedPhotoName, setUploadedPhotoName] = useState(''); // Name of the uploaded file for display
    const [cloudinaryPhotoUrl, setCloudinaryPhotoUrl] = useState(''); // URL from Cloudinary
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false); // Loading state for photo upload

    // State for form fields
    const [area, setArea] = useState('');
    const [type, setType] = useState('unsafe act'); // Default to 'unsafe-act'
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [category, setCategory] = useState('');

    // State for AI description
    const [isGettingAIDescription, setIsGettingAIDescription] = useState(false);

    // State for speech-to-text
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null); // Ref for SpeechRecognition object

    const { _id } = useSelector((state) => state.profile.user);

    // Define categories based on the image provided by the user
    const unsafeActCategories = [
        'Behavior', 'Attitude', 'Negligence', 'Over Confidence',
        'Underestimation', 'Short cuts', 'Overlooking consequences', 'Procrastination'
    ];

    const unsafeConditionCategories = [
        'Lack of Knowledge', 'External Factors', 'Incompetency', 'Machine/Tool Condition',
        'Experience', 'Unsafe action by others', 'Natural calamity'
    ];

    // Fetch initial data (areas and users)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [areasRes, usersRes] = await Promise.all([
                    getAreas(),
                    getUsers()
                ]);
                setAreas(areasRes.data);
                setUsers(usersRes); // Users data might be used for other purposes, keeping it
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load required data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Initialize SpeechRecognition API
    useEffect(() => {
        // Check for browser compatibility
        if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false; // Stop after a single utterance
            recognitionRef.current.interimResults = false; // Only return final results
            recognitionRef.current.lang = 'en-US';

            // Event handler for when speech is recognized
            recognitionRef.current.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map((result) => result[0].transcript)
                    .join('');
                setDescription((prevDesc) => prevDesc + (prevDesc ? ' ' : '') + transcript); // Append to existing description
            };

            // Event handler for when speech recognition ends
            recognitionRef.current.onend = () => {
                setIsListening(false);
            };

            // Event handler for errors
            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                toast.error(`Speech recognition error: ${event.error}`);
                setIsListening(false);
            };
        } else {
            console.warn('Speech Recognition API not supported in this browser.');
            toast.warn('Speech-to-text is not supported in your browser.');
        }

        // Cleanup on component unmount
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    // Handle photo selection and immediate upload to Cloudinary
    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhoto(file);
            setUploadedPhotoName(file.name);
            setIsUploadingPhoto(true);
            setCloudinaryPhotoUrl(''); // Clear previous URL

            try {
                const uploadResult = await uploadEvidenceToServer(file);
                if (uploadResult) {
                    setCloudinaryPhotoUrl(uploadResult);
                    toast.success('Photo uploaded successfully!');
                } else {
                    toast.error('Failed to upload photo to Cloudinary.');
                    setPhoto(null);
                    setUploadedPhotoName('');
                }
            } catch (error) {
                console.error('Error uploading photo:', error);
                toast.error('Error uploading photo. Please try again.');
                setPhoto(null);
                setUploadedPhotoName('');
            } finally {
                setIsUploadingPhoto(false);
            }
        }
    };

    const removePhoto = () => {
        setPhoto(null);
        setUploadedPhotoName('');
        setCloudinaryPhotoUrl('');
    };

    // Handle getting AI description for the uploaded image
    // const handleGetAIDescription = async () => {
    //     if (!cloudinaryPhotoUrl) {
    //         toast.error('Please upload a photo first to get AI description.');
    //         return;
    //     }

    //     setIsGettingAIDescription(true);
    //     try {
    //         const descriptionResult = await getImageDescriptionFromUrl(cloudinaryPhotoUrl);
    //         if (descriptionResult && descriptionResult.description) {
    //             setDescription(descriptionResult.description);
    //             toast.success('AI description generated!');
    //         } else {
    //             toast.error('Failed to get AI description. Please try again.');
    //         }
    //     } catch (error) {
    //         console.error('Error getting AI description:', error);
    //         toast.error('Error getting AI description. Please try again.');
    //     } finally {
    //         setIsGettingAIDescription(false);
    //     }
    // };

    // Handle speech-to-text functionality
    const toggleSpeechRecognition = () => {
        if (recognitionRef.current) {
            if (isListening) {
                recognitionRef.current.stop();
                setIsListening(false);
            } else {
                // Removed: setDescription(''); // This line was clearing the description
                recognitionRef.current.start();
                setIsListening(true);
                toast.success('Listening for speech...');
            }
        } else {
            toast.warn('Speech recognition is not available.');
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!category) {
            toast.error('Please select a category');
            return;
        }

        if (!area || !description) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSubmitting(true);

        try {
            // Construct the observation data to be sent
            const observationData = {
                area,
                type,
                description,
                photos: cloudinaryPhotoUrl ? [cloudinaryPhotoUrl] : [], // Use the Cloudinary URL
                notes,
                category,
                observedBy: _id,
            };

            await createObservation(observationData);

            toast.success('Observation submitted successfully');
            navigate('/user-dashboard');
        } catch (error) {
            console.error('Error submitting observation:', error);
            toast.error('Failed to submit observation');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const getCategories = () => {
        if (type === 'unsafe act') {
            return unsafeActCategories;
        } else if (type === 'unsafe condition') {
            return unsafeConditionCategories;
        } else {
            return [];
        }
    };

    return (
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 font-inter">
            <div className="mb-6">
                <h1 className="text-3xl font-extrabold text-gray-900">Submit Observation</h1>
                <p className="text-gray-600 mt-2">Record a new safety observation with detailed information and AI assistance.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {/* Area Selection */}
                        <div>
                            <label htmlFor="area" className="block text-sm font-semibold text-gray-700 mb-1">
                                Area <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="area"
                                value={area}
                                onChange={(e) => setArea(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                                required
                            >
                                <option value="">Select an area</option>
                                {areas.map((area) => (
                                    <option key={area._id} value={area._id}>
                                        {area.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Observation Type Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Observation Type <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-2 flex flex-wrap gap-4">
                                <label className="inline-flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        value="unsafe act"
                                        checked={type === 'unsafe act'}
                                        onChange={(e) => { setType(e.target.value); setCategory(''); }}
                                        className="form-radio h-5 w-5 text-yellow-600 border-gray-300 focus:ring-yellow-500 rounded-full"
                                    />
                                    <span className="ml-2 text-gray-800">Unsafe Act</span>
                                </label>
                                <label className="inline-flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        value="unsafe condition"
                                        checked={type === 'unsafe condition'}
                                        onChange={(e) => { setType(e.target.value); setCategory(''); }}
                                        className="form-radio h-5 w-5 text-red-600 border-gray-300 focus:ring-red-500 rounded-full"
                                    />
                                    <span className="ml-2 text-gray-800">Unsafe Condition</span>
                                </label>
                            </div>
                        </div>

                        {/* Category Selection */}
                        <div>
                            <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-1">
                                Category <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                                required
                            >
                                <option value="">Select a category</option>
                                {getCategories().map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                         {/* Photo Upload */}
                        <div className="col-span-1 md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">
                                Photo
                            </label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md bg-gray-50">
                                <div className="space-y-1 text-center">
                                    {cloudinaryPhotoUrl ? (
                                        <img src={cloudinaryPhotoUrl} alt="Uploaded" className="mx-auto h-24 w-auto object-cover rounded-md shadow-sm" />
                                    ) : (
                                        <Camera className="mx-auto h-12 w-12 text-gray-400" />
                                    )}
                                    <div className="flex text-sm text-gray-600">
                                        <label
                                            htmlFor="photo"
                                            className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                        >
                                            <span>{isUploadingPhoto ? 'Uploading...' : 'Upload a file'}</span>
                                            <input
                                                id="photo"
                                                name="photo"
                                                type="file"
                                                className="sr-only"
                                                accept="image/*"
                                                onChange={handlePhotoUpload}
                                                disabled={isUploadingPhoto}
                                            />
                                        </label>
                                        <p className="pl-1">or drag and drop</p>
                                    </div>
                                    <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                                </div>
                            </div>

                            {uploadedPhotoName && (
                                <div className="mt-4 bg-blue-50 p-3 rounded-md flex items-center justify-between border border-blue-200 shadow-sm">
                                    <div className="flex items-center">
                                        <Upload className="h-5 w-5 text-blue-500 mr-2" />
                                        <span className="text-sm text-blue-800 truncate">{uploadedPhotoName}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removePhoto}
                                        className="text-blue-400 hover:text-blue-600 p-1 rounded-full hover:bg-blue-100 transition duration-200"
                                        title="Remove photo"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Description with AI and Mic */}
                        <div className="col-span-1 md:col-span-2">
                            <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-1">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <textarea
                                    id="description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={4}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out pr-10" // Add padding for icons
                                    placeholder="Describe what you observed..."
                                    required
                                ></textarea>
                                <div className="absolute top-2 right-2 flex space-x-2">
                                    <button
                                        type="button"
                                        // onClick={handleGetAIDescription}
                                        disabled={!cloudinaryPhotoUrl || isGettingAIDescription || isUploadingPhoto}
                                        className={`p-2 rounded-full transition duration-200 ease-in-out ${
                                            !cloudinaryPhotoUrl || isGettingAIDescription || isUploadingPhoto
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                        }`}
                                        title="Get AI Description"
                                    >
                                        {isGettingAIDescription ? (
                                            <div className="animate-spin h-5 w-5 border-t-2 border-b-2 border-blue-600 rounded-full"></div>
                                        ) : (
                                            <Brain className="h-5 w-5" />
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={toggleSpeechRecognition}
                                        disabled={!recognitionRef.current}
                                        className={`p-2 rounded-full transition duration-200 ease-in-out ${
                                            !recognitionRef.current
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : isListening
                                                ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                                : 'bg-green-100 text-green-600 hover:bg-green-200'
                                        }`}
                                        title={isListening ? "Stop Listening" : "Start Speech-to-Text"}
                                    >
                                        {isListening ? <MicOff className="h-5 w-5 animate-pulse" /> : <Mic className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                       

                        {/* Additional Notes */}
                        <div className="col-span-1 md:col-span-2">
                            <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-1">
                                Additional Notes
                            </label>
                            <textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out"
                                placeholder="Any additional information..."
                            ></textarea>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-3 mt-8">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ease-in-out"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting || isUploadingPhoto || isGettingAIDescription || isListening}
                        className={`px-6 py-2 rounded-lg text-white font-medium shadow-md transition duration-200 ease-in-out ${
                            submitting || isUploadingPhoto || isGettingAIDescription || isListening
                                ? 'bg-blue-300 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        }`}
                    >
                        {submitting ? 'Submitting...' : 'Submit Observation'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SubmitObservation;
